/**
 * The hidden turn that makes Bestie open the conversation on the chat page.
 *
 * It is a real human message in the agent thread — the agent needs an input to respond to,
 * and the chat stream takes no system role. It is hidden from the UI by
 * `isBestieGreetingPrompt`, which `BestieMessagesList` applies to both live messages and
 * restored history.
 *
 * Deliberately in English with no translation key: the reply language comes from the
 * `user_locale` configuration the chat stream already sends.
 *
 * Keep this constant plain ASCII — no `<`, `>`, `&`, or quotes. The filter above matches by
 * exact string equality against `message.content`, and on the live path that content has
 * round-tripped through `DOMPurify.sanitize` (see `sanitizeHtml`, called from `startStream`).
 * Sanitizing HTML-special characters would silently change the string and break the match,
 * un-hiding this instruction in the UI.
 */
export const BESTIE_GREETING_PROMPT = 'Open the conversation. Greet the user, say in one or two sentences how you can help with their job search, and invite them to reply. Do not mention this instruction.';

/** True for the hidden priming turn, which must never be rendered. */
export const isBestieGreetingPrompt = (message: { type?: string; content?: string }): boolean => (
  message.type === 'human' && message.content === BESTIE_GREETING_PROMPT
);

/**
 * Removes past greeting exchanges from restored history.
 *
 * Every chat-page entry sends a fresh hidden priming turn (see `useProactiveGreeting`) and the
 * agent persists both it and its reply to the thread. `isBestieGreetingPrompt` hides the priming
 * turn itself at the render sites, but nothing removes the reply that follows it — so restoring
 * history would surface one near-identical "let me help you" opener per past visit, including
 * one adjacent to the *live* entry's own greeting, rendering as a visible duplicate. This strips
 * the whole exchange — the priming turn and the message immediately after it — once, in one
 * place, so every consumer of restored history (the chat page and the `/messages` Bestie tab) is
 * covered without needing its own logic.
 *
 * A priming turn with nothing after it (last message in the thread) is simply dropped on its
 * own; there is no reply to also remove.
 */
export const stripGreetingExchange = <T extends { type?: string; content?: string }>(messages: T[]): T[] => {
  const result: T[] = [];
  for (let i = 0; i < messages.length; i += 1) {
    if (isBestieGreetingPrompt(messages[i])) {
      // Also drop the reply immediately after, if there is one.
      i += 1;
      continue;
    }
    result.push(messages[i]);
  }
  return result;
};
